import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { BedDouble, Bath, Maximize, MapPin, Star, Play } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import axios from 'axios';
import Footer from '../components/sections/Footer';
import SEO from '../components/SEO';
import { getOrganizationSchema, getBreadcrumbSchema } from '../seo/schemas';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Portfolio = () => {
  const { i18n } = useTranslation();
  const isZh = i18n.language === 'zh';
  const [projects, setProjects] = useState([]);
  const [bedFilter, setBedFilter] = useState(null);
  const [sqftMax, setSqftMax] = useState(1200);
  const [areaFilter, setAreaFilter] = useState('');
  const [regionFilter, setRegionFilter] = useState('');
  const [imgIdx, setImgIdx] = useState({});

  useEffect(() => {
    const load = async () => {
      try {
        const r = await axios.get(`${API}/admin/projects`);
        setProjects(r.data.projects);
      } catch {}
    };
    load();
  }, []);

  const filtered = projects.filter(p => {
    if (bedFilter && p.bedrooms !== bedFilter) return false;
    if (p.sqft > sqftMax) return false;
    if (regionFilter && p.region !== regionFilter) return false;
    if (areaFilter && !(p.area || '').toLowerCase().includes(areaFilter.toLowerCase()) && !(p.address || '').toLowerCase().includes(areaFilter.toLowerCase())) return false;
    return true;
  });

  const bedOptions = [...new Set(projects.map(p => p.bedrooms))].sort();
  const getIdx = (id) => imgIdx[id] || 0;
  const setIdx = (id, v) => setImgIdx(prev => ({...prev, [id]: v}));

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <SEO
        title={isZh ? 'ALIGCUS ADU 项目案例 | 加州 1500+ 后院屋实拍 | 帕萨迪纳·阿凯迪亚·圣盖博' : 'ALIGCUS ADU Project Gallery | 1500+ California Backyard ADU Builds | Pasadena · Arcadia · San Gabriel'}
        description={isZh ? '浏览 ALIGCUS 在加州各地完成的 ADU 项目实拍：帕萨迪纳、阿凯迪亚、圣盖博、戴蒙德吧、湾区等。按房型、面积、地区筛选。' : 'Browse ALIGCUS completed ADU projects across California: Pasadena, Arcadia, San Gabriel, Diamond Bar, Bay Area. Filter by bedrooms, sqft, and region.'}
        canonical={isZh ? 'https://www.aligcus.com/zh/portfolio' : 'https://www.aligcus.com/portfolio'}
        lang={isZh ? 'zh' : 'en'}
        alternates={[
          { lang: 'en', url: 'https://www.aligcus.com/portfolio' },
          { lang: 'zh-CN', url: 'https://www.aligcus.com/zh/portfolio' },
        ]}
        schemas={[
          getOrganizationSchema(isZh ? 'zh' : 'en'),
          getBreadcrumbSchema([
            { name: isZh ? '首页' : 'Home', url: isZh ? 'https://www.aligcus.com/zh/' : 'https://www.aligcus.com/' },
            { name: isZh ? '项目案例' : 'Portfolio', url: isZh ? 'https://www.aligcus.com/zh/portfolio' : 'https://www.aligcus.com/portfolio' },
          ]),
        ]}
      />
      <section className="relative bg-gray-900 text-white py-24 pt-32">
        <div className="absolute inset-0 opacity-20">
          <img src="https://static.wixstatic.com/media/2de339_6c14e4a4ed3447ebac68c93238edfb7c~mv2.jpg" alt="" className="w-full h-full object-cover" />
        </div>
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold mb-4">{isZh ? '全部精选项目' : 'All Featured Projects'}</h1>
          <p className="text-lg text-gray-300">{isZh ? '探索我们在加州各地完成的ADU项目' : 'Explore our completed ADU projects across California'}</p>
        </div>
      </section>

      <main className="flex-grow py-10">
        <div className="max-w-7xl mx-auto px-6">
          {/* Filters */}
          <div className="flex flex-wrap gap-4 items-end mb-8 p-4 bg-gray-50 rounded-xl">
            {/* Region */}
            <div>
              <label className="text-xs font-bold text-gray-500 uppercase block mb-1">{isZh ? '区域' : 'Region'}</label>
              <div className="flex gap-1">
                <button onClick={() => setRegionFilter('')} className={`px-3 py-1.5 rounded-full text-xs font-medium ${!regionFilter ? 'bg-red-600 text-white' : 'bg-white text-gray-600 border'}`}>{isZh ? '全部' : 'All'}</button>
                {[{v:'South',en:'SoCal',zh:'南加'},{v:'North',en:'NorCal',zh:'北加'},{v:'San Diego',en:'San Diego',zh:'圣地亚哥'}].map(r => (
                  <button key={r.v} onClick={() => setRegionFilter(r.v)} className={`px-3 py-1.5 rounded-full text-xs font-medium ${regionFilter === r.v ? 'bg-red-600 text-white' : 'bg-white text-gray-600 border'}`}>{isZh ? r.zh : r.en}</button>
                ))}
              </div>
            </div>
            {/* Bedrooms */}
            <div>
              <label className="text-xs font-bold text-gray-500 uppercase block mb-1">{isZh ? '卧室' : 'Bedrooms'}</label>
              <div className="flex gap-1">
                <button onClick={() => setBedFilter(null)} className={`px-3 py-1.5 rounded-full text-xs font-medium ${!bedFilter ? 'bg-red-600 text-white' : 'bg-white text-gray-600 border'}`}>{isZh ? '全部' : 'All'}</button>
                {bedOptions.map(b => <button key={b} onClick={() => setBedFilter(b)} className={`px-3 py-1.5 rounded-full text-xs font-medium ${bedFilter === b ? 'bg-red-600 text-white' : 'bg-white text-gray-600 border'}`}>{b}{isZh ? '卧' : ' Bed'}</button>)}
              </div>
            </div>
            {/* Sqft slider */}
            <div className="flex-1 min-w-[200px]">
              <label className="text-xs font-bold text-gray-500 uppercase block mb-1">{isZh ? '最大面积' : 'Max Sq.ft'}: {sqftMax}</label>
              <input type="range" min={300} max={1200} step={50} value={sqftMax} onChange={e => setSqftMax(+e.target.value)} className="w-full accent-red-600" />
            </div>
            {/* Area text */}
            <div>
              <label className="text-xs font-bold text-gray-500 uppercase block mb-1">{isZh ? '地区' : 'Area'}</label>
              <input type="text" value={areaFilter} onChange={e => setAreaFilter(e.target.value)} placeholder={isZh ? '输入城市/地区...' : 'City / Area...'} className="px-3 py-1.5 border rounded-lg text-sm w-40 focus:border-red-600 outline-none" />
            </div>
          </div>

          {/* Projects Grid */}
          {filtered.length === 0 ? (
            <p className="text-center text-gray-400 py-20">{isZh ? '没有匹配的项目' : 'No matching projects'}</p>
          ) : (
            <div className="grid md:grid-cols-2 gap-8">
              {filtered.map(p => {
                const ci = getIdx(p.id);
                const imgs = p.images || [];
                return (
                  <Card key={p.id} className="overflow-hidden border-0 shadow-lg hover:shadow-2xl transition-all group">
                    {/* Image Carousel */}
                    <div className="relative h-72 overflow-hidden">
                      {imgs.length > 0 && <img src={imgs[ci % imgs.length]} alt={p.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />}
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4">
                        {p.area && <div className="flex items-center gap-2 text-white text-xl font-bold"><MapPin className="w-5 h-5" />{p.area}</div>}
                      </div>
                      {imgs.length > 1 && (
                        <>
                          <button type="button" onClick={() => setIdx(p.id, (ci - 1 + imgs.length) % imgs.length)} className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/40 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity text-sm">&lt;</button>
                          <button type="button" onClick={() => setIdx(p.id, (ci + 1) % imgs.length)} className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/40 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity text-sm">&gt;</button>
                          <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex gap-1">{imgs.map((_, i) => <div key={i} className={`w-1.5 h-1.5 rounded-full ${i === ci % imgs.length ? 'bg-white' : 'bg-white/40'}`} />)}</div>
                        </>
                      )}
                      {/* Rating */}
                      {p.rating > 0 && (
                        <div className="absolute top-3 right-3 flex gap-0.5">{[...Array(p.rating)].map((_, i) => <Star key={i} className="w-3.5 h-3.5 fill-yellow-400 text-yellow-400" />)}</div>
                      )}
                    </div>

                    <CardContent className="p-5">
                      <h3 className="font-bold text-lg text-gray-900 mb-1">{p.title}</h3>
                      <div className="flex items-center gap-5 text-lg text-gray-600 mb-3">
                        {p.bedrooms > 0 && <span className="flex items-center gap-2"><BedDouble className="w-8 h-8 text-red-600" /><span className="text-xl font-semibold">{p.bedrooms}</span></span>}
                        {p.bathrooms > 0 && <span className="flex items-center gap-2"><Bath className="w-8 h-8 text-red-600" /><span className="text-xl font-semibold">{p.bathrooms}</span></span>}
                        {p.sqft > 0 && <span className="flex items-center gap-2"><Maximize className="w-8 h-8 text-red-600" /><span className="text-xl font-semibold">{p.sqft}sf</span></span>}
                        {p.area && <span className="text-gray-400 text-lg">| {p.area}</span>}
                      </div>
                      <p className="text-gray-600 text-sm leading-relaxed mb-3">{isZh ? p.description_zh : p.description_en}</p>
                      <div className="flex gap-2">
                        {p.vr_link && <a href={p.vr_link} target="_blank" rel="noopener noreferrer"><Button className="bg-red-600 hover:bg-red-700 text-white font-bold px-4 py-1.5 text-sm">VR</Button></a>}
                        {p.video_link && (
                          <a href={p.video_link} target="_blank" rel="noopener noreferrer">
                            <Button variant="outline" className="border-red-600 text-red-600 hover:bg-red-50 font-bold px-4 py-1.5 text-sm"><Play className="w-3.5 h-3.5 mr-1" />{isZh ? '视频' : 'Video'}</Button>
                          </a>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Portfolio;
